// AI quality and cost client. Shapes mirror packages/server/src/routes/ai-quality.ts
// (golden-question RAG evaluation + cost-per-resolution report, per site) and the
// super-admin price table in routes/system-config.ts.

import { apiFetch } from './api';

// ─── RAG evaluation (golden questions) ───

export type EvalMode = 'vector' | 'keyword';

export interface EvalCase {
  id: string;
  siteId: string;
  question: string;
  /** Knowledge entries a good answer needs; a case passes when one is in the top k. */
  expectedEntryIds: string[];
  /** Phrases the retrieved text must contain (fact-style cases). */
  mustInclude: string[];
  /** Drafts are not run until someone reviews them and marks them active. */
  status: 'active' | 'draft';
  source: 'admin' | 'import' | 'conversation' | 'generated';
  createdAt: string;
  updatedAt: string;
}

export interface EvalCaseInput {
  question: string;
  expectedEntryIds?: string[];
  mustInclude?: string[];
  status?: 'active' | 'draft';
}

export interface RetrievalMetrics {
  cases: number;
  hitRate: number;
  mrr: number | null;
  recall: number | null;
  factRecall: number | null;
}

export type ModeMetrics = RetrievalMetrics | { unavailable: string };

/** `{}` until the run finishes. */
export interface RunMetrics {
  modes?: Partial<Record<EvalMode, ModeMetrics>>;
  grading?:
    | { graded: number; ungraded: number; avgScore: number | null; groundedRate: number | null }
    | { unavailable: string };
}

export interface EvalRun {
  id: string;
  siteId: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  k: number;
  modes: EvalMode[];
  gradeAnswers: boolean;
  caseCount: number;
  metrics: RunMetrics;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface EvalResult {
  caseId: string;
  mode: EvalMode;
  question: string;
  expectedEntryIds: string[];
  mustInclude: string[];
  retrievedEntryIds: string[];
  hit: boolean;
  rank: number | null;
  recall: number | null;
  factRecall: number | null;
  answer: string | null;
  grade: { score: number; grounded: boolean; reason: string } | null;
}

const evalBase = (siteId: string) => `/api/admin/sites/${siteId}/rag-eval`;

export const listEvalCases = (siteId: string) =>
  apiFetch<{ cases: EvalCase[] }>(`${evalBase(siteId)}/cases`).then((r) => r.cases);

export const createEvalCase = (siteId: string, input: EvalCaseInput) =>
  apiFetch<{ case: EvalCase }>(`${evalBase(siteId)}/cases`, { method: 'POST', body: JSON.stringify(input) }).then(
    (r) => r.case,
  );

export const updateEvalCase = (siteId: string, caseId: string, patch: Partial<EvalCaseInput>) =>
  apiFetch<{ case: EvalCase }>(`${evalBase(siteId)}/cases/${caseId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }).then((r) => r.case);

export const deleteEvalCase = (siteId: string, caseId: string) =>
  apiFetch(`${evalBase(siteId)}/cases/${caseId}`, { method: 'DELETE' });

/** One draft question per knowledge entry no case covers yet. */
export const draftEvalCases = (siteId: string) =>
  apiFetch<{ drafted: number }>(`${evalBase(siteId)}/cases/draft`, { method: 'POST' }).then((r) => r.drafted);

export const listEvalRuns = (siteId: string) =>
  apiFetch<{ runs: EvalRun[] }>(`${evalBase(siteId)}/runs`).then((r) => r.runs);

export const startEvalRun = (siteId: string, opts: { k: number; modes: EvalMode[]; gradeAnswers: boolean }) =>
  apiFetch<{ runId: string }>(`${evalBase(siteId)}/runs`, { method: 'POST', body: JSON.stringify(opts) }).then(
    (r) => r.runId,
  );

export const getEvalRun = (siteId: string, runId: string) =>
  apiFetch<{ run: EvalRun; results: EvalResult[] }>(`${evalBase(siteId)}/runs/${runId}`);

// ─── cost per resolution ───

export type Outcome = 'ai_resolved' | 'human_resolved' | 'ticketed' | 'abandoned';

export interface CostReport {
  currency: 'USD';
  range: { from: string; to: string };
  conversations: number;
  byOutcome: Record<Outcome, number>;
  aiResolutionRate: number | null;
  aiAssistedConversations: number;
  aiEscalations: number;
  aiEscalationRate: number | null;
  intentConfidenceDistribution: { low: number; medium: number; high: number; unknown: number };
  conversationSpend: number;
  overheadSpend: number;
  agentHours: number;
  agentCost: number | null;
  perResolution: {
    aiCostPerAiResolution: number | null;
    aiCostPerResolution: number | null;
    blendedCostPerResolution: number | null;
  };
  medianCostByOutcome: Record<Outcome, number | null>;
  byIntent: Array<{
    key: string | null;
    name: string;
    conversations: number;
    aiResolved: number;
    humanResolved: number;
    ticketed: number;
    abandoned: number;
    spend: number;
  }>;
  byModel: Array<{
    provider: string;
    model: string;
    calls: number;
    inputTokens: number;
    outputTokens: number;
    /** null = no price for this model; its calls are left out of every total. */
    cost: number | null;
  }>;
  daily: Array<{ date: string; spend: number; aiResolved: number; humanResolved: number }>;
  unknownUsageCalls: number;
  unpricedCalls: number;
  retrievals: number;
  toolCalls: number;
  /** Ended chats whose outcome job has not run yet — not in the numbers. */
  pendingOutcomes: number;
}

export const getCostReport = (siteId: string, fromISO: string, toISO: string) =>
  apiFetch<CostReport>(
    `/api/admin/sites/${siteId}/cost?${new URLSearchParams({ from: fromISO, to: toISO }).toString()}`,
  );

// ─── model prices (super-admin) ───

export interface ModelPrice {
  id: string;
  provider: string;
  model: string;
  /** USD per million tokens. */
  inputPerMtok: number;
  outputPerMtok: number;
  effectiveFrom: string;
  source: string | null;
  updatedAt: string;
}

export interface ModelPriceInput {
  provider: string;
  model: string;
  inputPerMtok: number;
  outputPerMtok: number;
  effectiveFrom?: string;
  source?: string | null;
}

const pricesBase = '/api/admin/system-config/model-prices';

export const listModelPrices = () => apiFetch<{ prices: ModelPrice[] }>(pricesBase).then((r) => r.prices);

export const createModelPrice = (input: ModelPriceInput) =>
  apiFetch<{ price: ModelPrice }>(pricesBase, { method: 'POST', body: JSON.stringify(input) }).then((r) => r.price);

export const updateModelPrice = (
  id: string,
  patch: Partial<Pick<ModelPriceInput, 'inputPerMtok' | 'outputPerMtok' | 'source'>>,
) =>
  apiFetch<{ price: ModelPrice }>(`${pricesBase}/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }).then(
    (r) => r.price,
  );

export const deleteModelPrice = (id: string) => apiFetch(`${pricesBase}/${id}`, { method: 'DELETE' });
